import { Box } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { HOLIDAY_GROUPS } from "../../../theme/tokens";
import { formatNameList } from "../../../utils/formatNameList";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { HolidayEffectsList } from "./HolidayEffectsList";
import { HolidaySmsField, NO_SMS, smsMissingText, smsToSend } from "./HolidaySmsField";
import { HolidaySummaryCard } from "./HolidaySummaryCard";
import type { Holiday, HolidayApplyResult, HolidayGroup } from "./holidayApi";
import { useApplyHolidays } from "./holidayQueries";

/** Up to this many holidays are named in the title; more read as "4 holidays". */
const NAMES_IN_TITLE = 3;
const EFFECT_KEYS = ["holidays.apply.effect.remove", "holidays.apply.effect.add", "holidays.apply.effect.skip", "holidays.apply.effect.noOpenShifts"] as const;

type Props = {
	holidays: readonly Holiday[];
	groups: readonly HolidayGroup[];
	onClose: () => void;
	/** After the server answers, e.g. to clear the selection. */
	onApplied: () => void;
};

/** "Apply Christmas to the schedule?" — the holidays, what applying does, and an optional SMS. */
export const HolidayApplyDialog: FC<Props> = ({ holidays, groups, onClose, onApplied }) => {
	const { t, i18n } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const apply = useApplyHolidays();
	const [sms, setSms] = useState(NO_SMS);
	const title =
		holidays.length <= NAMES_IN_TITLE
			? t("holidays.apply.title", {
					names: formatNameList(
						holidays.map((holiday) => holiday.name),
						i18n.language,
					),
				})
			: t("holidays.apply.titleMany", { count: holidays.length });

	// Each holiday is applied on its own: say which went on the schedule and why any didn't.
	const handleApplied = (results: HolidayApplyResult[]) => {
		const nameOf = new Map(holidays.map((holiday) => [holiday.id, holiday.name]));
		const appliedIds = new Set(results.filter((result) => result.errorKey === null).map((result) => result.holidayId));
		const applied = holidays.filter((holiday) => appliedIds.has(holiday.id)).map((holiday) => holiday.name);
		if (applied.length > 0) showSnackbar(t("holidays.apply.done", { names: formatNameList(applied, i18n.language) }), "success");
		for (const result of results) {
			if (result.errorKey !== null) showSnackbar(t("holidays.apply.failed", { name: nameOf.get(result.holidayId), reason: t(result.errorKey) }), "error");
		}
		onApplied();
		onClose();
	};

	const handleConfirm = () => {
		if (smsMissingText(sms)) {
			showSnackbar(t("holidays.sms.missing"), "warning");
			return;
		}
		apply.mutate({ holidayIds: holidays.map((holiday) => holiday.id), smsMessage: smsToSend(sms) }, { onSuccess: handleApplied });
	};

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: { sm: HOLIDAY_GROUPS.DIALOG_BODY_MIN_WIDTH_PX } }}>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
				{holidays.map((holiday) => (
					<HolidaySummaryCard key={holiday.id} holiday={holiday} groups={groups} />
				))}
			</Box>
			<HolidayEffectsList heading={t("holidays.apply.whatHeading")} lines={EFFECT_KEYS.map((key) => t(key))} />
			<HolidaySmsField sms={sms} onChange={setSms} label={t("holidays.sms.label")} disabled={apply.isPending} dataTestId="holiday-apply-sms" />
		</Box>
	);

	return (
		<SystemDialog
			open
			onClose={onClose}
			title={title}
			body={body}
			describeBody
			cancel={t("cancel")}
			confirm={sms.on ? t("holidays.apply.confirmWithSms") : t("holidays.apply.confirm")}
			confirmButtonVariant="primary"
			onConfirm={handleConfirm}
			confirmLoading={apply.isPending}
			maxWidth="sm"
			dataTestId="holiday-apply-dialog"
			confirmDataTestId="holiday-apply-confirm"
			cancelDataTestId="holiday-apply-cancel"
		/>
	);
};
